"use client";

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    userService,
    type ProvisionMyProfileInput,
    type ProvisionMyProfileResult,
} from '../services/users';
import { adminApiService } from '../services/adminApi';
import { IUser, LocationRole, TenantRole, User } from '@order-up/models';
import { CreateUserType } from '@order-up/schemas';
import { useEffect } from 'react';
import { authKeys } from './useAuth';
import { adminUserKeys } from './useAdminUsers';
import { createLogger } from '../utils/logger';

const logger = createLogger('useUsers');

/**
 * Defines the query keys used for user-related data.
 */
export const userKeys = {
    all: ['users'] as const,
    details: () => [...userKeys.all, 'detail'] as const,
    detailByUid: (uid: string) => [...userKeys.details(), uid] as const,
};

/**
 * A hook to fetch a user's profile by their UID.
 * @param uid The UID of the user. Can be null or undefined if the user is not logged in.
 */
/** How often to re-ask while `users/{uid}` is absent. */
const ABSENT_PROFILE_POLL_MS = 2_500;
/** ~60s of polling — long enough for a slow signup, short enough to stop. */
const ABSENT_PROFILE_POLL_ATTEMPTS = 24;

/**
 * How long this hook keeps retrying an absent profile, end to end.
 *
 * EXPORTED because any surface that gives up on a missing profile has to outlast
 * it. The auth page's ceiling was a hard-coded 15s while this polls for 60, so
 * the page declared PERMANENT failure with 45 seconds of legitimate retrying
 * still to run — and a just-created account, the exact case this poll exists
 * for, was the one that lost. Deriving that ceiling from this constant is what
 * stops the two drifting apart again.
 */
export const ABSENT_PROFILE_POLL_WINDOW_MS =
    ABSENT_PROFILE_POLL_MS * ABSENT_PROFILE_POLL_ATTEMPTS;

export const useGetUserByUid = (uid: string | null | undefined) => {
    return useQuery({
        queryKey: userKeys.detailByUid(uid!),
        queryFn: () => userService.getUserByUid(uid!),
        // This query should only run when the uid is a non-empty string.
        enabled: !!uid,
        // A NULL here is a race, not an answer. During sign-up this query
        // routinely runs before `users/{uid}` is committed and resolves null;
        // with the global 5-minute staleTime that null is then served for the
        // whole window, and the ONLY thing that heals it is the onSnapshot
        // listener in `useUserSubscription`. When that listener dies (App Check
        // cold start → permission-denied), nothing recovers and the user sits
        // on "we couldn't load your profile" while their profile exists —
        // observed in production 2026-08-08.
        //
        // Treating an absent profile as immediately stale costs one cheap read
        // on a genuinely profile-less account (site admins, who are routed by
        // claim before this matters) and removes the stranding entirely.
        staleTime: (query) => (query.state.data == null ? 0 : 5 * 60 * 1000),
        // …AND SOMETHING HAS TO ACTUALLY ASK AGAIN.
        //
        // `staleTime: 0` only MARKS the null stale — React Query still refetches
        // solely on a new observer mount, window focus, reconnect or an
        // invalidation. This app disables focus refetching
        // (`AppProviders.tsx:145`) and the auth page stays mounted, so a stale
        // null was never re-read: a user whose profile existed sat on "we
        // couldn't load your profile" until the Firebase ID token refreshed an
        // HOUR later and shook the auth state loose. That hour is the tell —
        // nothing in between was retrying.
        //
        // Polling only while the profile is ABSENT makes "the account exists ⇒
        // the user is signed in" true within seconds, without the caller having
        // to know it needs to retry. It costs nothing in the normal case: the
        // moment data is non-null the interval turns off. Bounded by attempt
        // count so a genuinely profile-less account (a site admin, who is routed
        // by claim before this matters) cannot poll forever.
        refetchInterval: (query) =>
            query.state.data == null &&
            query.state.dataUpdateCount < ABSENT_PROFILE_POLL_ATTEMPTS
                ? ABSENT_PROFILE_POLL_MS
                : false,
        // The poll must survive a backgrounded tab — a signup that finishes
        // while the user is reading the verification email in another window is
        // the common case, not the edge one.
        refetchIntervalInBackground: true,
    });
};

/**
 * A hook that subscribes to real-time updates of a user's profile.
 * This should be used once per user session to keep their data fresh.
 * @param uid The UID of the user to subscribe to.
 */
export const useUserSubscription = (uid: string | null | undefined) => {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!uid) {
            return;
        }

        const unsubscribe = userService.subscribeToUser(
            uid,
            (user: User | null) => {
                logger.debug('Received update for user. Updating cache', { uid });
                queryClient.setQueryData(userKeys.detailByUid(uid), user);
            },
            // The listener is the self-heal; if IT fails, the self-heal is what
            // broke. Fall back to the query — invalidating makes React Query
            // refetch through the normal path (which carries its own App Check
            // cold-start retry), so a transient listener failure no longer
            // strands a cached null forever.
            (error: Error) => {
                logger.warn('User subscription failed — falling back to a refetch', {
                    uid,
                    error: error.message,
                });
                void queryClient.invalidateQueries({
                    queryKey: userKeys.detailByUid(uid),
                });
            },
        );

        // Cleanup the subscription when the component unmounts or the UID changes.
        return () => unsubscribe();

    }, [uid, queryClient]);
};

/**
 * Create — or HEAL — the signed-in user's own `users/{uid}` document, on the
 * SERVER (`POST /api/v1/users/me/provision`).
 *
 * Use this anywhere the CURRENT user needs a profile. It is the repair for the
 * class of account that reaches production with a Firebase Auth record, correct
 * custom claims, and no `users/{uid}` document — the residue of a sign-up whose
 * browser Firestore write was lost with nothing to retry it.
 *
 * SAFE TO CALL WHEN THE PROFILE MIGHT ALREADY EXIST, and that is not a
 * concession — it is the point. A browser cannot tell "absent" from
 * "unreadable": under ENFORCED App Check a token-less Firestore read is not
 * rejected with a catchable error, the SDK falls back to the empty local cache
 * and reports a perfectly healthy document as ABSENT. The server does the real
 * existence check and returns the stored profile with `created: false`, so a
 * client that was wrong is CORRECTED rather than duplicated.
 *
 * On success the profile query cache is primed with the returned `User` — which
 * is what resolves the false-absent case on the spot: the browser gets handed
 * the document it could not read.
 */
export const useProvisionMyProfile = () => {
    const queryClient = useQueryClient();
    // `| void` is deliberate. The SIGN-UP caller passes the name the person just
    // typed; the HEAL caller has no form to read and passes nothing. A default
    // parameter does not express that to React Query — `mutate(undefined)` is a
    // type error against an inferred `TVariables` — so the optionality is
    // declared here rather than worked around at each call site.
    return useMutation<ProvisionMyProfileResult, Error, ProvisionMyProfileInput | void>({
        mutationFn: (input) => userService.provisionMyProfile(input ?? {}),
        onSuccess: ({ user, created }) => {
            logger.info('Own profile provisioned', { uid: user.uid, created });
            // Prime, then invalidate. Priming closes the window where a query
            // that resolved `null` a moment ago keeps serving it; the
            // invalidate lets the normal read confirm.
            queryClient.setQueryData(userKeys.detailByUid(user.uid), user);
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(user.uid) });
        },
        onError: (error) => {
            logger.error('Own profile provisioning failed', error);
        },
    });
};

/**
 * A hook to create a user profile in Firestore.
 *
 * ⚠️ ADMIN TOOLING ONLY — this writes a profile for SOMEONE ELSE from the
 * browser. For the signed-in user's own profile use
 * {@link useProvisionMyProfile}; a browser Firestore write has no server-side
 * repair.
 *
 * @returns A mutation object from TanStack Query.
 */
export const useCreateUserProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (userData: CreateUserType) => userService.createUserProfile(userData),
        onSuccess: (_, variables) => {
            logger.info('Success. Invalidating user query', { uid: variables.uid });
            // Invalidate the specific user's query to refetch their new profile.
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
        },
        onError: (error) => {
            logger.error('Failed', error);
        }
    });
};

/**
 * A hook to update an existing user's profile in Firestore.
 * @returns A mutation object from TanStack Query.
 */
export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, userData }: { uid: string; userData: Partial<Omit<IUser, 'uid' | 'createdAt'>> }) =>
            userService.updateUser(uid, userData),
        onSuccess: async (_, variables) => {
            logger.info('Success. Invalidating user query', { uid: variables.uid });

            // Invalidate the specific user's query to refetch their updated profile
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });

            // Check if we updated fields that need Firebase Auth sync
            const needsAuthSync = variables.userData.displayName !== undefined ||
                                 variables.userData.photoURL !== undefined ||
                                 variables.userData.email !== undefined;

            if (needsAuthSync) {
                logger.debug('Profile fields updated, triggering Firebase Auth sync', { uid: variables.uid });

                // Trigger auth state refresh to run the automatic sync
                // This will cause useAuthSubscription to re-run and sync Firestore → Firebase Auth
                queryClient.invalidateQueries({ queryKey: authKeys.user() });

                // Alternative: We could also call the manual sync here, but the auto-sync should handle it
            }
        },
        onError: (error) => {
            logger.error('Failed', error);
        }
    });
};

/**
 * A hook to update the CURRENT user's own profile/preferences — get-or-create.
 *
 * Use this from the self-service account pages (Profile / Preferences). Unlike
 * {@link useUpdateUser} (admin tooling updating an existing user by uid), this
 * seeds a valid `users/{uid}` doc from the Firebase Auth identity when none
 * exists yet — so the shared account UI works for HQ admins on the separate
 * `admins/{uid}` plane too. `seed` is only consulted on the create path.
 */
export const useUpsertUserProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, userData, seed }: {
            uid: string;
            userData: Partial<Omit<IUser, 'uid' | 'createdAt'>>;
            seed: { email: string; emailVerified?: boolean };
        }) => userService.upsertUserProfile(uid, userData, seed),
        onSuccess: async (_, variables) => {
            logger.info('Profile upsert success. Invalidating user query', { uid: variables.uid });
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });

            // Sync to Firebase Auth when the synced profile fields change.
            const needsAuthSync = variables.userData.displayName !== undefined ||
                                 variables.userData.photoURL !== undefined ||
                                 variables.userData.email !== undefined;
            if (needsAuthSync) {
                queryClient.invalidateQueries({ queryKey: authKeys.user() });
            }
        },
        onError: (error) => {
            logger.error('Profile upsert failed', error);
        }
    });
};

/**
 * A hook to set a user's email as verified in Firestore.
 * This is a dedicated hook for the specific email verification flow.
 * @returns A mutation object from TanStack Query.
 */
export const useSetUserEmailVerified = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (uid: string) => userService.setUserEmailVerified(uid),
        onSuccess: (_, uid) => {
            logger.info('Success. Invalidating user query', { uid });
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(uid) });
        },
        onError: (error) => {
            logger.error('Failed', error);
        }
    });
};

/**
 * Grants a tenant-scoped assignment to a user. Site-admin-only operation —
 * enforced server-side by the `requireAdmin` middleware on the API route.
 */
export const useGrantTenantAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, tenantDocId, role }: {
            uid: string; tenantDocId: string; role: TenantRole;
        }) => userService.grantTenantAssignment(uid, tenantDocId, role),
        onSuccess: (_, variables) => {
            logger.info('Granted tenant assignment', { uid: variables.uid });
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Grant failed', error); },
    });
};

/**
 * Grants a restaurant-scoped assignment.
 */
export const useGrantLocationAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            uid, tenantDocId, restaurantDocId: locationDocId, role,
        }: {
            uid: string; tenantDocId: string; restaurantDocId: string; role: LocationRole;
        }) => userService.grantRestaurantAssignment(uid, tenantDocId, locationDocId, role),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Grant failed', error); },
    });
};

/**
 * Revokes a tenant-scoped assignment. Optional `successorUid` satisfies
 * the W11 last-owner gate when revoking the sole active Owner of a
 * tenant — the API returns `LAST_OWNER_BLOCKED` otherwise so the UI
 * can prompt for a successor and retry.
 */
export const useRevokeTenantAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, tenantDocId, reason, successorUid }: {
            uid: string; tenantDocId: string; reason?: string; successorUid?: string;
        }) => userService.revokeTenantAssignment(uid, tenantDocId, reason, successorUid),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Revoke failed', error); },
    });
};

/**
 * Revokes a restaurant-scoped assignment. `successorUid` is accepted
 * for API symmetry with tenant-scoped revoke; restaurant scope does
 * not trigger the last-owner check, so it's effectively a no-op here.
 */
export const useRevokeLocationAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            uid, tenantDocId, restaurantDocId: locationDocId, reason, successorUid,
        }: {
            uid: string; tenantDocId: string; restaurantDocId: string; reason?: string; successorUid?: string;
        }) => userService.revokeRestaurantAssignment(uid, tenantDocId, locationDocId, reason, successorUid),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Revoke failed', error); },
    });
};

/**
 * Suspends a tenant-scoped assignment.
 */
export const useSuspendTenantAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, tenantDocId, reason }: {
            uid: string; tenantDocId: string; reason?: string;
        }) => userService.suspendTenantAssignment(uid, tenantDocId, reason),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Suspend failed', error); },
    });
};

/**
 * Suspends a restaurant-scoped assignment.
 */
export const useSuspendLocationAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            uid, tenantDocId, restaurantDocId: locationDocId, reason,
        }: {
            uid: string; tenantDocId: string; restaurantDocId: string; reason?: string;
        }) => userService.suspendRestaurantAssignment(uid, tenantDocId, locationDocId, reason),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Suspend failed', error); },
    });
};

/**
 * Unsuspends (reinstates) a tenant-scoped assignment.
 */
export const useUnsuspendTenantAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid, tenantDocId }: {
            uid: string; tenantDocId: string;
        }) => userService.unsuspendTenantAssignment(uid, tenantDocId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Unsuspend failed', error); },
    });
};

/**
 * Unsuspends (reinstates) a restaurant-scoped assignment.
 */
export const useUnsuspendLocationAssignment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            uid, tenantDocId, restaurantDocId: locationDocId,
        }: {
            uid: string; tenantDocId: string; restaurantDocId: string;
        }) => userService.unsuspendRestaurantAssignment(uid, tenantDocId, locationDocId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(variables.uid) });
            queryClient.invalidateQueries({ queryKey: userKeys.all });
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
        },
        onError: (error) => { logger.error('Unsuspend failed', error); },
    });
};

/**
 * Fetches the assignment audit history for a user.
 */
export const useAssignmentHistory = (uid: string | undefined) => {
    return useQuery({
        queryKey: [...userKeys.all, 'assignment-history', uid],
        queryFn: () => adminApiService.getAssignmentHistory(uid!),
        enabled: !!uid,
    });
};

/**
 * A hook to fetch a list of all tenant users.
 * @param count The maximum number of users to fetch.
 */
export const useFetchTenantUsers = (count: number) => {
    return useQuery({
        queryKey: [...userKeys.all, { count }],
        queryFn: () => userService.fetchTenantUsers(count),
    });
};

/**
 * A hook to search for tenant users by display name or email.
 * @param searchTerm The term to search for.
 */
export const useSearchTenantUsers = (searchTerm?: string) => {
    return useQuery({
        queryKey: [...userKeys.all, 'search', { searchTerm }],
        queryFn: () => userService.searchTenantUsers(searchTerm!),
        enabled: !!searchTerm && searchTerm.length >= 2,
    });
};

/**
 * A hook to deactivate a user.
 */
export const useDeactivateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid }: { uid: string; email: string }) => userService.deactivateUser(uid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: userKeys.all });
        },
    });
};

/**
 * A hook to delete a user's Firestore document.
 * This only removes the document, not the Firebase Auth user.
 * @returns A mutation object from TanStack Query.
 */
export const useDeleteUserDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (uid: string) => userService.deleteUserDocument(uid),
        onSuccess: (_, uid) => {
            logger.info('Success. Invalidating user queries', { uid });
            // Invalidate the specific user's query
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(uid) });
            // Also invalidate all users list since the user is deleted
            queryClient.invalidateQueries({ queryKey: userKeys.all });
        },
        onError: (error) => {
            logger.error('Failed', error);
        }
    });
};

/**
 * A hook to completely delete a user (both Auth and Document).
 * Note: Firebase Auth deletion requires Admin SDK and is not implemented yet.
 * @returns A mutation object from TanStack Query.
 */
export const useDeleteUserCompletely = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (uid: string) => userService.deleteUserCompletely(uid),
        onSuccess: (_, uid) => {
            logger.info('Success. Invalidating user queries', { uid });
            // Invalidate the specific user's query
            queryClient.invalidateQueries({ queryKey: userKeys.detailByUid(uid) });
            // Also invalidate all users list since the user is deleted
            queryClient.invalidateQueries({ queryKey: userKeys.all });
        },
        onError: (error) => {
            logger.error('Failed', error);
        }
    });
};

/**
 * A hook to reactivate a user.
 */
export const useReactivateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ uid }: { uid: string; email: string }) => userService.reactivateUser(uid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: userKeys.all });
        },
    });
};
